import React, { useState, useEffect,useMemo } from "react";
import { API_BASE, EXEC_APPENDER } from "../utils/constants";
import {stripCodeWrappers, fetchTextWithDiagnostics, looksLikeCode } from "../utils/helpers";
import { Shell, PrimaryButton, SecondaryButton, DangerLink, Chip, Skeleton } from "../components/UI";
import { CodeBlock } from "../components/CodeBlock";

export function FlashCardViewer({
  token,
  section,
  category,
  onBack,
  onLogout,
  onUnauthorized,
  appName,
  isDarkMode,
}) {
  const [cards, setCards] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadAll() {
      setLoading(true);
      setError("");
      try {
        const base = API_BASE.replace(/\/$/, "");
        const url = `${base}/flashcards${EXEC_APPENDER}/${encodeURIComponent(
          appName
        )}/${encodeURIComponent(section)}/${encodeURIComponent(
          category
        )}?CurrentExamID=0&prevNext=Next`.replace(/\+/g, "%2B");

        const res = await fetchTextWithDiagnostics(url, {
          headers: { Authorization: token },
        });
        if (res.status === 401) return onUnauthorized?.();
        if (!res.ok)
          return setError(`Failed to load (status ${res.status})`);

        const arr = JSON.parse(res.text);
        setCards(Array.isArray(arr) ? arr : []);
        setIndex(0);
      } catch (e) {
        setError("Bad data");
      } finally {
        setLoading(false);
      }
    }
    loadAll();
  }, [token, section, category, appName, onUnauthorized]);

  const current = cards[index] || {};
  const heading =
    current.heading ||
    current.title ||
    current.topic ||
    `${section} / ${category}`;

  const rawBlocks = useMemo(
    () =>
      [
        current.explaination1,
        current.explaination2,
        current.explaination3,
        current.explanation,
        current.body,
      ]
        .filter(Boolean)
        .map(String),
    [current]
  );

  const blocks = useMemo(
    () =>
      rawBlocks.map((b) => ({
        type: looksLikeCode(stripCodeWrappers(b)) ? "code" : "text",
        content: b,
      })),
    [rawBlocks]
  );

  const answer = current.answer || current.solution || null;

  const prev = () => setIndex((i) => Math.max(0, i - 1));
  const next = () => setIndex((i) => Math.min(cards.length - 1, i + 1));

  return (
    <div className="p-5">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={onBack}
          className={`text-sm font-semibold hover:underline ${isDarkMode ? "text-indigo-200" : "text-indigo-700"}`}
        >
          ← Back
        </button>
        <div className="flex items-center gap-3">
          <Chip>
            {cards.length ? `Card ${index + 1} / ${cards.length}` : "Flashcards"}
          </Chip>
          <DangerLink
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onLogout();
            }}
          >
            Logout
          </DangerLink>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-8 w-1/2" />
          <Skeleton className="h-44 w-full" />
          <Skeleton className="h-10 w-44" />
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">
          {error}
        </div>
      ) : cards.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-700">
          No flashcards found.
        </div>
      ) : (
        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="px-6 py-5 border-b border-slate-100">
            <div className="flex items-center gap-2 flex-wrap">
              <Chip>Flashcard</Chip>
              <Chip>{section}</Chip>
              <Chip>{category}</Chip>
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 mt-3">
              {heading}
            </h2>
          </div>

          <div className="px-6 py-6 space-y-4">
            {blocks.map(({ type, content }, idx) =>
              type === "code" ? (
                <CodeBlock key={idx}>{content}</CodeBlock>
              ) : (
                <div
                  key={idx}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                >
                  <div className="text-[11px] uppercase tracking-wide text-slate-600 font-semibold mb-2">
                    Explanation
                  </div>
                  {/<\/?[a-z][\s\S]*>/i.test(String(content)) ? (
                    <div
                      className="prose max-w-none"
                      dangerouslySetInnerHTML={{ __html: String(content) }}
                    />
                  ) : (
                    <div className="whitespace-pre-wrap text-slate-800 leading-relaxed">
                      {content}
                    </div>
                  )}
                </div>
              )
            )}

            {answer ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                <div className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold mb-2">
                  Answer
                </div>
                <div className="whitespace-pre-wrap text-slate-900">
                  {String(answer)}
                </div>
              </div>
            ) : null}
          </div>

          <div className="px-6 pb-6 flex items-center justify-between">
            <SecondaryButton onClick={prev} disabled={index === 0} title="Go to previous flashcard">
              ← Prev
            </SecondaryButton>
            <PrimaryButton
              onClick={next}
              disabled={index === cards.length - 1}
              title="Go to next flashcard"
            >
              Next →
            </PrimaryButton>
          </div>
        </div>
      )}
    </div>
  );
}

// =====================
// Exec helpers
// =====================
async function getOrCreateExecId({
  token,
  appName,
  section,
  category,
  onUnauthorized,
}) {
  const base = API_BASE.replace(/\/$/, "");

  const searchUrl =
    `${base}/exams${EXEC_APPENDER}/${encodeURIComponent(appName)}` +
    `/users/search_exam_exec${EXEC_APPENDER}` +
    `?category_name=${encodeURIComponent(category)}` +
    `&section_name=${encodeURIComponent(section)}` +
    `&is_completed=N`;

  const searchRes = await fetchTextWithDiagnostics(searchUrl, {
    headers: { Authorization: token },
  });
  if (searchRes.status === 401) return onUnauthorized?.(), null;

  if (searchRes.ok) {
    try {
      const arr = JSON.parse(searchRes.text);
      const list = Array.isArray(arr) ? arr : [];
      if (list.length > 0) {
        list.sort(
          (a, b) =>
            Number(b?.exec_id ?? b?.execId ?? 0) -
            Number(a?.exec_id ?? a?.execId ?? 0)
        );
        const existing = list[0]?.exec_id ?? list[0]?.execId ?? null;
        if (existing != null) return Number(existing);
      }
    } catch {}
  }

  const createUrl =
    `${base}/exams${EXEC_APPENDER}/${encodeURIComponent(appName)}` +
    `/users/exam_exec${EXEC_APPENDER}`;

  const payload = {
    app_name: appName,
    category_name: category,
    section_name: section,
    is_completed: "N",
  };

  const createRes = await fetchTextWithDiagnostics(createUrl, {
    method: "POST",
    headers: {
      Authorization: token,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (createRes.status === 401) return onUnauthorized?.(), null;
  if (!createRes.ok) return null;

  try {
    const obj = JSON.parse(createRes.text);
    const createdId = obj?.exec_id ?? obj?.execId ?? obj?.id ?? null;
    return createdId != null ? Number(createdId) : null;
  } catch {
    return null;
  }
}

// =====================
// Questions (your same logic)
// =====================
